/**
 * "Name it" after Ctrl+S (W2-10 projects-data; DESIGN.md §5.13).
 *
 * `project.checkpoint` makes an unnamed checkpoint at once; the "Name it" action of its toast runs
 * `project.nameCheckpoint` with the checkpoint's seq, which leaves a naming request here and opens the Versions
 * dialog. The dialog picks the request up and shows that version with its name field ready. A version can be named
 * only while it is still the chart's current state: persistence names a version by checkpointing again at the same
 * seq (the records merge and the new label wins).
 *
 * Requests are kept per persistence client (one per app; tests get their own) and per project.
 */

import type { ProjectId } from '../../core/model/types';

/** A version to name: its seq, and whether it is the checkpoint Ctrl+S just made ("Name it"). */
export interface NamingRequest {
  readonly seq: number;
  readonly justMade: boolean;
}

const requests = new WeakMap<object, Map<ProjectId, NamingRequest>>();

function of(owner: object): Map<ProjectId, NamingRequest> {
  let map = requests.get(owner);
  if (map === undefined) {
    map = new Map();
    requests.set(owner, map);
  }
  return map;
}

/** Asks the next Versions dialog of `projectId` to name a version. */
export function requestNaming(owner: object, projectId: ProjectId, request: NamingRequest): void {
  of(owner).set(projectId, request);
}

/** The pending naming request of `projectId` (left in place; see `clearNamingRequest`). */
export function namingRequest(owner: object, projectId: ProjectId): NamingRequest | null {
  return of(owner).get(projectId) ?? null;
}

/** Drops the request once a dialog has picked it up. */
export function clearNamingRequest(owner: object, projectId: ProjectId): void {
  of(owner).delete(projectId);
}
